import { useLayoutEffect, useRef, useState } from "react";
import { Box, Chip } from "@mui/material";
import { shopCategories } from "../utility/constants";

// Matches the mobile CategoryFilterRow timing so expand/collapse feels the same.
const DURATION_MS = 300;
const EASING = "cubic-bezier(0.4, 0, 0.2, 1)";
const ROW_GAP_PX = 8;
const PADDING_Y_PX = 4;

export const CategoryFilterChip = ({ label, selected, onClick }) => (
  <Chip
    label={label}
    size="small"
    onClick={onClick}
    sx={{
      flexShrink: 0,
      cursor: "pointer",
      bgcolor: selected ? "#046287" : undefined,
      color: selected ? "white" : undefined,
      "&:hover": { bgcolor: selected ? "#046287" : undefined },
    }}
  />
);

// Renders `categories` as toggleable chips: two rows that scroll horizontally
// together by default, or a wrapped grid four rows tall that scrolls
// vertically when `expanded`. The parent owns `expanded` (FilterPanel ties it
// to its "More filters" header). Mirrors ngwaahia-mobile's
// components/ui/CategoryFilterRow.js.
const CategoryFilterRow = ({
  categories = shopCategories,
  selectedCategories,
  onToggleCategory,
  expanded,
}) => {
  const outerRef = useRef(null);
  const scrollRef = useRef(null);
  // Measured from a rendered chip so the row heights follow the theme's chip
  // size rather than a hardcoded guess.
  const [chipHeight, setChipHeight] = useState(0);
  // Height of the collapsed rows' horizontal scrollbar (0 where scrollbars
  // overlay content, e.g. macOS trackpads and phones).
  const [scrollbarHeight, setScrollbarHeight] = useState(0);

  useLayoutEffect(() => {
    const chip = outerRef.current?.querySelector(".MuiChip-root");
    if (chip && !chipHeight) setChipHeight(chip.offsetHeight);
    const scroller = scrollRef.current;
    if (scroller) {
      setScrollbarHeight(scroller.offsetHeight - scroller.clientHeight);
    }
  }, [expanded, chipHeight]);

  // Bring the first pre-selected category into view (e.g. search opened from
  // a product's "search this category").
  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (expanded || !container || !selectedCategories.length) return;
    const index = categories.findIndex((c) =>
      selectedCategories.includes(c.value),
    );
    const chip = container.querySelectorAll(".MuiChip-root")[index];
    if (chip) container.scrollLeft = Math.max(0, chip.offsetLeft - 12);
    // Only on mount: later taps shouldn't yank the row around.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rowsHeight = (rows) =>
    rows * chipHeight + (rows - 1) * ROW_GAP_PX + PADDING_Y_PX * 2;

  const renderChip = (cat) => (
    <CategoryFilterChip
      key={cat.value}
      label={`${cat.icon} ${cat.label}`}
      selected={selectedCategories.includes(cat.value)}
      onClick={() => onToggleCategory(cat.value)}
    />
  );

  const half = Math.ceil(categories.length / 2);
  const rowSx = { display: "flex", gap: 0.75 };

  return (
    <Box
      ref={outerRef}
      sx={{
        height: chipHeight
          ? expanded
            ? rowsHeight(4)
            : rowsHeight(2) + scrollbarHeight
          : "auto",
        overflow: "hidden",
        transition: `height ${DURATION_MS}ms ${EASING}`,
      }}
    >
      {expanded ? (
        <Box
          sx={{
            height: "100%",
            overflowY: "auto",
            display: "flex",
            flexWrap: "wrap",
            alignContent: "flex-start",
            rowGap: `${ROW_GAP_PX}px`,
            columnGap: 0.75,
            py: `${PADDING_Y_PX}px`,
            scrollbarWidth: "thin",
          }}
        >
          {categories.map(renderChip)}
        </Box>
      ) : (
        <Box
          ref={scrollRef}
          sx={{
            position: "relative",
            overflowX: "auto",
            py: `${PADDING_Y_PX}px`,
            display: "flex",
            flexDirection: "column",
            rowGap: `${ROW_GAP_PX}px`,
            // Thin but visible, so mouse users can still drag it sideways.
            scrollbarWidth: "thin",
          }}
        >
          <Box sx={rowSx}>{categories.slice(0, half).map(renderChip)}</Box>
          <Box sx={rowSx}>{categories.slice(half).map(renderChip)}</Box>
        </Box>
      )}
    </Box>
  );
};

export default CategoryFilterRow;
